import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, DollarSign, TrendingUp, CreditCard, Users, BarChart3, CheckCircle2, AlertTriangle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function EarningsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [earnings, setEarnings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [markPaidRef, setMarkPaidRef] = useState("");
  const [markPaidNotes, setMarkPaidNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [earningsRes, txRes] = await Promise.all([
        apiClient.get("/payments/earnings"),
        apiClient.get("/payments/history"),
      ]);
      setEarnings(earningsRes.data);
      setTransactions(txRes.data);
    } catch { toast.error("Failed to load earnings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMarkAsPaid = async () => {
    if (!markingPaid) return;
    setProcessing(true);
    try {
      const res = await apiClient.post(`/payments/${markingPaid.id}/mark-paid`, {
        reference: markPaidRef || "admin_manual",
        notes: markPaidNotes,
      });
      toast.success(`Payment confirmed! Ticket created. Consultant: ${res.data.consultant_assigned || "None"}`);
      setMarkingPaid(null);
      setMarkPaidRef("");
      setMarkPaidNotes("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark payment");
    } finally {
      setProcessing(false);
    }
  };

  const pendingCount = transactions.filter(tx => tx.payment_status === "pending").length;

  const isAdmin = user?.role === "admin";

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="earnings-dashboard-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {isAdmin ? t("earnings.revenueEarnings") : t("earnings.myEarnings")}
            </h1>
            <p className="text-slate-500 mt-1">
              {isAdmin ? t("earnings.platformRevenue") : t("earnings.yourEarnings")}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading...</div>
        ) : (
          <>
            {/* Pending Transactions Alert */}
            {isAdmin && pendingCount > 0 && (
              <Card className="border-2 border-amber-300 bg-amber-50" data-testid="pending-alert">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">{pendingCount} Pending Transaction{pendingCount > 1 ? "s" : ""} — Action Required</p>
                    <p className="text-sm text-amber-600">These payments were not confirmed by the gateway. Use "Mark as Paid" if payment was received externally.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            {isAdmin && earnings && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600"><DollarSign className="h-6 w-6" /></div>
                  <div><p className="text-2xl font-bold font-heading">${(earnings.total_revenue || 0).toFixed(2)}</p><p className="text-sm text-slate-500">Total Revenue</p></div>
                </CardContent></Card>
                <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-sag-navy/10 text-sag-navy"><TrendingUp className="h-6 w-6" /></div>
                  <div><p className="text-2xl font-bold font-heading">${(earnings.total_sag || 0).toFixed(2)}</p><p className="text-sm text-slate-500">SAG Revenue (60%)</p></div>
                </CardContent></Card>
                <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-amber-100 text-amber-600"><Users className="h-6 w-6" /></div>
                  <div><p className="text-2xl font-bold font-heading">${(earnings.total_consultant_payouts || 0).toFixed(2)}</p><p className="text-sm text-slate-500">Consultant Payouts (40%)</p></div>
                </CardContent></Card>
                <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-blue-100 text-blue-600"><CreditCard className="h-6 w-6" /></div>
                  <div><p className="text-2xl font-bold font-heading">{earnings.transaction_count || 0}</p><p className="text-sm text-slate-500">Transactions</p></div>
                </CardContent></Card>
              </div>
            )}

            {!isAdmin && earnings && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600"><DollarSign className="h-6 w-6" /></div>
                  <div><p className="text-2xl font-bold font-heading">${(earnings.total_earnings || 0).toFixed(2)}</p><p className="text-sm text-slate-500">{t("earnings.totalEarned")}</p></div>
                </CardContent></Card>
                <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-amber-100 text-amber-600"><TrendingUp className="h-6 w-6" /></div>
                  <div><p className="text-2xl font-bold font-heading">${(earnings.pending_payout || 0).toFixed(2)}</p><p className="text-sm text-slate-500">{t("earnings.pendingPayout")}</p></div>
                </CardContent></Card>
                <Card className="sag-card"><CardContent className="p-5 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-blue-100 text-blue-600"><BarChart3 className="h-6 w-6" /></div>
                  <div><p className="text-2xl font-bold font-heading">{earnings.total_jobs || 0}</p><p className="text-sm text-slate-500">{t("earnings.jobsCompleted")}</p></div>
                </CardContent></Card>
              </div>
            )}

            {/* Consultant Breakdown (Admin only) */}
            {isAdmin && earnings?.consultant_breakdown?.length > 0 && (
              <Card className="sag-card">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg">{t("earnings.consultantEarnings")}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Consultant</TableHead>
                        <TableHead>Jobs</TableHead>
                        <TableHead>Earnings (40%)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {earnings.consultant_breakdown.map((c) => (
                        <TableRow key={c.consultant_id}>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell>{c.jobs}</TableCell>
                          <TableCell className="font-semibold text-emerald-600">${c.earnings.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Processor Breakdown (Admin only) */}
            {isAdmin && earnings?.processor_breakdown?.length > 0 && (
              <Card className="sag-card">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg">{t("earnings.byPaymentProcessor")}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Processor</TableHead>
                        <TableHead>Transactions</TableHead>
                        <TableHead>Volume</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {earnings.processor_breakdown.map((p) => (
                        <TableRow key={p.processor}>
                          <TableCell className="font-medium capitalize">{p.processor === "fiserv" ? "Scotiabank" : p.processor}</TableCell>
                          <TableCell>{p.count}</TableCell>
                          <TableCell className="font-semibold">${p.total.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Transaction History */}
            <Card className="sag-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg">{t("earnings.transactionHistory")}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {transactions.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">{t("earnings.noTransactions")}</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Service</TableHead>
                        {isAdmin && <TableHead>Client</TableHead>}
                        <TableHead>Processor</TableHead>
                        <TableHead>Amount</TableHead>
                        {isAdmin && <TableHead>SAG (60%)</TableHead>}
                        <TableHead>{isAdmin ? "Consultant (40%)" : "Your Share"}</TableHead>
                        <TableHead>Status</TableHead>
                        {isAdmin && <TableHead>Action</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow key={tx.id} className={tx.payment_status === "pending" ? "bg-amber-50/50" : ""}>
                          <TableCell className="text-sm text-slate-600">{new Date(tx.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="font-medium">{tx.service_name}</TableCell>
                          {isAdmin && <TableCell className="text-slate-600">{tx.client_name}</TableCell>}
                          <TableCell>
                            <Badge variant="secondary" className="capitalize">{tx.processor === "fiserv" ? "Scotiabank" : tx.processor}</Badge>
                          </TableCell>
                          <TableCell className="font-semibold">${tx.amount?.toFixed(2)}</TableCell>
                          {isAdmin && <TableCell className="text-sag-navy">${tx.revenue_sag?.toFixed(2)}</TableCell>}
                          <TableCell className="text-emerald-600">${tx.revenue_consultant?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={
                              tx.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                              tx.payment_status === "pending" ? "bg-amber-100 text-amber-700" :
                              "bg-red-100 text-red-700"
                            }>
                              {tx.payment_status}
                              {tx.manual_override && " (Manual)"}
                            </Badge>
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              {tx.payment_status === "pending" && (
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => setMarkingPaid(tx)}
                                  data-testid={`mark-paid-${tx.id}`}
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark as Paid
                                </Button>
                              )}
                              {tx.payment_status === "paid" && tx.ticket_id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/tickets/${tx.ticket_id}`)}
                                >
                                  View Ticket
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Mark as Paid Dialog */}
            <Dialog open={!!markingPaid} onOpenChange={(open) => { if (!open) { setMarkingPaid(null); setMarkPaidRef(""); setMarkPaidNotes(""); } }}>
              <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                  <DialogTitle>Confirm Payment Received</DialogTitle>
                  <DialogDescription>Manually mark this transaction as paid and create a support ticket</DialogDescription>
                </DialogHeader>
                {markingPaid && (
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <p className="text-slate-500">Client:</p>
                        <p className="font-medium">{markingPaid.client_name}</p>
                        <p className="text-slate-500">Service:</p>
                        <p className="font-medium">{markingPaid.service_name}</p>
                        <p className="text-slate-500">Amount:</p>
                        <p className="font-bold text-lg text-emerald-700">${markingPaid.amount?.toFixed(2)}</p>
                        <p className="text-slate-500">Processor:</p>
                        <p className="font-medium capitalize">{markingPaid.processor === "fiserv" ? "Scotiabank" : markingPaid.processor}</p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Payment Reference</label>
                      <Input
                        placeholder="e.g., Bank transfer #, external payment ID"
                        value={markPaidRef}
                        onChange={(e) => setMarkPaidRef(e.target.value)}
                        data-testid="mark-paid-reference"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
                      <Textarea
                        placeholder="e.g., Client paid via external Stripe link, Scotiabank transfer received..."
                        value={markPaidNotes}
                        onChange={(e) => setMarkPaidNotes(e.target.value)}
                        rows={2}
                        data-testid="mark-paid-notes"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={handleMarkAsPaid}
                        disabled={processing}
                        data-testid="confirm-mark-paid-btn"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1.5" />
                        {processing ? "Processing..." : "Confirm Payment & Create Ticket"}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 text-center">This will mark the payment as confirmed, create a support ticket, and auto-assign a consultant. A confirmation email will be sent to the client.</p>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
